/**
 * Admin Activity Log Component
 *
 * Displays recent administrative actions for audit and tracking.
 * Shows who did what and when for security and compliance.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect } from 'react';
import { Card, Badge, Spinner, Button } from 'flowbite-react';
import { HiClock, HiUser, HiDocument } from 'react-icons/hi';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface ActivityLogEntry {
  id: string;
  timestamp: string;
  admin_email: string;
  action: string;
  resource_type: string;
  resource_id: string;
  details?: string;
  ip_address?: string;
}

interface AdminActivityLogProps {
  limit?: number;
  showFilters?: boolean;
}

const AdminActivityLog: React.FC<AdminActivityLogProps> = ({
  limit = 10,
  showFilters = false,
}) => {
  const [activities, setActivities] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchActivityLog();
  }, [limit]);

  const fetchActivityLog = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call actual API - fail loud if backend unavailable
      const response = await Make_Authorized_API_Call<ActivityLogEntry>(
        API_ENDPOINTS.SUPERADMIN.AUDIT,
        'GET',
        undefined,
        { params: { limit: limit.toString() } }
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to fetch audit log');
      }

      setActivities(response.data.Records || []);
    } catch (err) {
      console.error('Failed to fetch activity log:', err);
      setError(err instanceof Error ? err.message : 'Failed to load activity log');
    } finally {
      setLoading(false);
    }
  };

  const getActionBadgeColor = (action: string): 'success' | 'info' | 'failure' | 'gray' => {
    switch (action) {
      case 'CREATE':
        return 'success';
      case 'UPDATE':
        return 'info';
      case 'DELETE':
        return 'failure';
      case 'VIEW':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const formatTimeAgo = (timestamp: string): string => {
    const now = Date.now();
    const then = new Date(timestamp).getTime();
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <Card className="bg-white">
        <div className="flex items-center justify-center p-6">
          <Spinner size="lg" />
          <span className="ml-3 text-gray-700">Loading activity log...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-white">
        <div className="text-center p-6">
          <p className="text-red-600">{error}</p>
          <Button size="sm" color="light" onClick={fetchActivityLog} className="mt-3">
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            <p className="text-sm text-gray-500">
              Administrative actions and audit trail
            </p>
          </div>
          <Badge color="gray">Last {limit} actions</Badge>
        </div>

        <div className="space-y-3">
          {activities.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              No recent activity
            </div>
          ) : (
            activities.map((activity) => (
              <div
                key={activity.id}
                className="flex items-start p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <div className="flex-shrink-0 mt-1">
                  <HiDocument className="w-5 h-5 text-gray-400" />
                </div>
                <div className="ml-3 flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-2">
                      <Badge color={getActionBadgeColor(activity.action)}>
                        {activity.action}
                      </Badge>
                      <span className="text-sm font-medium text-gray-900">
                        {activity.resource_type}
                      </span>
                    </div>
                    <div className="flex items-center text-xs text-gray-500">
                      <HiClock className="w-3 h-3 mr-1" />
                      {formatTimeAgo(activity.timestamp)}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">{activity.details}</p>
                  <div className="flex items-center mt-1 text-xs text-gray-500">
                    <HiUser className="w-3 h-3 mr-1" />
                    {activity.admin_email}
                    {activity.resource_id && (
                      <>
                        <span className="mx-2">•</span>
                        <span className="font-mono">{activity.resource_id}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="pt-3 border-t border-gray-200 flex justify-between items-center">
          <button
            onClick={fetchActivityLog}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Refresh
          </button>
          <button className="text-sm text-gray-600 hover:text-gray-700 font-medium">
            View Full Log
          </button>
        </div>
      </div>
    </Card>
  );
};

export default AdminActivityLog;
